import useOverlay, {OverlayState} from "@/hooks/common/useOverlay";
import {useTaggingLoad} from "@/hooks/common/useTagging";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import Alert from "@COMPONENTS/common/modal/alert/Desktop";
import EventListPopup from "@COMPONENTS/product/common/popup/desktop/EventListPopup/EventListPopup";
import * as style from "@COMPONENTS/style/productlist/compareproduct";
import {TextButton} from "@FRAMEWORK/ui/desktop/button";
import {EditorBox} from "@FRAMEWORK/ui/desktop/editor";
import {Flex} from "@FRAMEWORK/ui/desktop/layout";
import {PopupContainer} from "@FRAMEWORK/ui/desktop/popup";
import {TableContainer} from "@FRAMEWORK/ui/desktop/table";
import {eventCompareAsync} from "@QUERIES/prdt/event/useEventCompare";
import {GoodsCalendar, GoodsCompare} from "@QUERIES/prdt/goods/useGoodsCompare";
import {goodsAvailEventAsync} from "@QUERIES/prdt/goods/useGoodsOneAvailEvent";
import {format} from "@yb/utils";
import {useRouter} from "next/router";
import React, {useMemo, useState} from "react";
import LocalGoodsCompareCalendar from "../goods/LocalCompareGoodsCalendar";
import CompareEventPopup from "./CompareEventPopup";

interface Paramters {
	calendarList: Array<GoodsCalendar>;
	compareList: Array<GoodsCompare>;
}

type EventCompare = {
	compareGoodsCd: string;
	compareDate: string;
	compareDspSid: string;
	compareSingleTypeCd: string;
};

const CompareProductsPopupComponent = (props: OverlayState & Paramters) => {
	const {compareList, calendarList, ...overlayProps} = props;
	const [eventCompare, setEventCompare] = useState<Array<EventCompare>>([]);
	const [open] = useOverlay();
	const router = useRouter();

	const onDateChange = (e: React.ChangeEvent<HTMLElement>) => {
		const {goodsCd, dspSid, date, singleTypeCd} = e.target.dataset;
		if (!goodsCd) return;
		const existingIndex = eventCompare?.findIndex(v => v.compareGoodsCd === goodsCd);

		if (existingIndex !== -1) {
			setEventCompare(prevEventCompare => {
				const updatedEventCompare = [...prevEventCompare];
				const existingData = updatedEventCompare[existingIndex];

				// 다른 데이터 수정
				if (dspSid) existingData.compareDspSid = dspSid;
				if (date) existingData.compareDate = date;
				if (singleTypeCd) existingData.compareSingleTypeCd = singleTypeCd;

				return updatedEventCompare;
			});
		} else {
			// 추가
			setEventCompare(prevEventCompare => [
				...prevEventCompare,
				{
					compareGoodsCd: goodsCd,
					compareDspSid: dspSid || "",
					compareDate: date || "",
					compareSingleTypeCd: singleTypeCd || "",
				},
			]);
		}
	};
	// 출발일 기준 상품 비교하기
	const handleClick = async () => {
		if (compareList.length !== eventCompare.length) {
			open(prop => <Alert {...prop} text="출발일을 모두 선택해주세요." />);
			return false;
		}
		const compareGoodsCd = eventCompare.map(item => item.compareGoodsCd).join(",");
		const compareDspSid = eventCompare.map(item => item.compareDspSid).join(",");
		const compareDate = eventCompare.map(item => item.compareDate).join(",");
		const compareSingleTypeCd = eventCompare.map(item => item.compareSingleTypeCd).join(",");

		const obj = {
			compareGoodsCd,
			compareDspSid,
			compareDate,
			compareSingleTypeCd,
		};

		const result = await eventCompareAsync(obj);
		const {body} = result.data;

		if (body) {
			overlayProps.close();
			open(prop => <CompareEventPopup {...prop} {...body} />);
		}
	};

	const getCalData: Record<string, Array<GoodsCalendar>> = useMemo(() => {
		const result = {};
		compareList.forEach(item => {
			const cal = calendarList.filter(v => v.goodsCd === item.goodsCd);
			result[item.goodsCd] = cal;
		});
		return result;
	}, [calendarList]);

	const Colgroup = useMemo(() => {
		if (compareList && compareList.length === 2) {
			return (
				<colgroup>
					<col style={{width: "8%"}} />
					<col style={{width: "8%"}} />
					<col style={{width: "42%"}} />
					<col style={{width: "42%"}} />
				</colgroup>
			);
		} else if (compareList && compareList.length === 3) {
			return (
				<colgroup>
					<col style={{width: "9%"}} />
					<col style={{width: "7%"}} />
					<col style={{width: "28%"}} />
					<col style={{width: "28%"}} />
					<col style={{width: "28%"}} />
				</colgroup>
			);
		}
	}, [compareList]);

	// 출발일 보기
	const [slide] = useOverlay("slideIn");
	const handleEventList = async goodsCd => {
		const result = await goodsAvailEventAsync(goodsCd);
		const {body} = result.data;
		slide(prop => <EventListPopup {...prop} goodsCd={goodsCd} availEvent={body} />);
	};

	useTaggingLoad("pkg_compare_goodsCd", compareList);

	return (
		<>
			{compareList && (
				<PopupContainer {...overlayProps} title="비교하기" size="large" paddingTop={false}>
					<style.TableWrap>
						<TableContainer lineTop={false} types="vertical">
							<caption>상품 비교</caption>
							{Colgroup}
							<tbody>
								<tr>
									<th scope="row" colSpan={2}>
										상품번호
									</th>
									{compareList.map((item, index) => (
										<td key={index}>{item.goodsCd}</td>
									))}
								</tr>
								<tr>
									<th scope="row" colSpan={2}>
										상품명
									</th>
									{compareList.map((item, index) => (
										<td key={index}>
											<style.ProductExplain>{item.goodsNm}</style.ProductExplain>
										</td>
									))}
								</tr>
								<tr>
									<th scope="row" rowSpan={3}>
										상품가
									</th>
									<th scope="row">성인</th>
									{compareList.map((item, index) => (
										<td key={index}>
											<style.ProductPrice>
												<em>{format.koKRFormatNumber(item.minAdtPrice)}</em> 원 ~{" "}
												<em>{format.koKRFormatNumber(item.maxAdtPrice)}</em> 원
											</style.ProductPrice>
										</td>
									))}
								</tr>
								<tr>
									<th scope="row">아동</th>
									{compareList.map((item, index) => (
										<td key={index}>
											<style.ProductPrice>
												<em>{format.koKRFormatNumber(item.minChdPrice)}</em> 원 ~{" "}
												<em>{format.koKRFormatNumber(item.maxChdPrice)}</em> 원
											</style.ProductPrice>
										</td>
									))}
								</tr>
								<tr>
									<th scope="row">유아</th>
									{compareList.map((item, index) => (
										<td key={index}>
											<style.ProductPrice>
												<em>{format.koKRFormatNumber(item.minInfPrice)}</em> 원 ~{" "}
												<em>{format.koKRFormatNumber(item.maxInfPrice)}</em> 원
											</style.ProductPrice>
										</td>
									))}
								</tr>
								<tr>
									<th scope="row" colSpan={2}>
										여행 기간
									</th>
									{compareList.map((item, index) => (
										<td key={index}>
											<div>{item.evNightNm}</div>
										</td>
									))}
								</tr>
								<tr>
									<th scope="row" colSpan={2}>
										방문 도시
									</th>
									{compareList.map((item, index) => (
										<td key={index}>
											<div>{item.vcityInfo}</div>
										</td>
									))}
								</tr>
								<tr>
									<th scope="row" colSpan={2}>
										여행포인트
									</th>
									{compareList.map((item, index) => (
										<td key={index}>
											<EditorBox dangerouslySetInnerHTML={{__html: item.prdExpl?.replace(/\r\n/g, "<br/>")}} />
										</td>
									))}
								</tr>
								<tr>
									<th scope="row" colSpan={2}>
										상품 확인
									</th>
									{compareList.map((item, index) => (
										<td key={index}>
											<Flex align="center" justify="center">
												<TextButton text="출발일 보기" onClick={() => handleEventList(item.goodsCd)} size="default" />
												<TextButton
													text="상품 상세보기"
													onClick={() => router.push(item.webLinkUrl)}
													size="default"
													types="basic"
												/>
											</Flex>
										</td>
									))}
								</tr>
								<tr>
									<th scope="row" colSpan={2}>
										출발일 선택
									</th>
									{compareList.map((item, index) => {
										const cal = getCalData[item.goodsCd];
										if (!cal || cal.length === 0) return <></>;
										const copyCal = [...cal];

										const min = copyCal.length < 2 ? copyCal[0].evStartDt : copyCal.shift().evStartDt;
										const max = copyCal.length < 2 ? copyCal[0].evStartDt : copyCal.pop().evStartDt;
										return (
											<td key={index}>
												<LocalGoodsCompareCalendar
													minDate={min}
													maxDate={max}
													data={cal}
													onDateChange={(date, e: React.ChangeEvent<HTMLElement>) => onDateChange(e)}
												/>
											</td>
										);
									})}
								</tr>
							</tbody>
						</TableContainer>
					</style.TableWrap>
					<style.BtnBlock>
						<TextButton text="출발일 기준 상품 비교하기" types="confirm" onClick={handleClick} />
					</style.BtnBlock>
				</PopupContainer>
			)}
		</>
	);
};

const CompareProductsPopup = (props: OverlayState & Paramters) => (
	<ErrorBoundary logMsg="CompareProductsPopup Error">
		<CompareProductsPopupComponent {...props} />
	</ErrorBoundary>
);

export default CompareProductsPopup;
